import { z } from 'zod';
import { optionalKrsSchema, optionalRegonSchema, optionalTaxIdSchema } from '../nip';
import {
  currencySchema,
  emailSchema,
  hexColorSchema,
  idSchema,
  markdownSchema,
  nameSchema,
  rateSchema,
  urlSchema,
  versionSchema,
} from './common';
import {
  COMPANY_REGISTRY_SOURCES,
  COMPANY_VAT_STATUSES,
  type CompanyAddress,
  companyAddressInputSchema,
  isEmptyAddress,
  parseAddressLine,
} from './company-registry';

/**
 * CRM: klienci i kontakty (docs/3VTOS_MVP.md §5, docs/DATA_MODEL.md „crm”).
 * Tabele: `clients`, `contacts` (packages/db/src/schema/crm.ts).
 *
 * Tu są enumy i schematy pól encji (klocki wejść). Wejścia i wyjścia usług (create/update/list, DTO)
 * dopisuje agent modułu crm w tym pliku.
 */

// ── Enumy ───────────────────────────────────────────────────────────────────
/** Statusy klienta: potencjalny, aktywny, nieaktywny, archiwalny. */
export const CLIENT_STATUSES = ['prospect', 'active', 'inactive', 'archived'] as const;
export type ClientStatus = (typeof CLIENT_STATUSES)[number];
export const clientStatusSchema = z.enum(CLIENT_STATUSES);

// ── Pola złożone (jsonb) ────────────────────────────────────────────────────
/**
 * `clients.company`: dane firmy (opcjonalne; Fala 7 — dane po NIP, DECISIONS 6.10.2026 „Dane firmy po NIP”).
 * Nazwa wyświetlana klienta (`clients.name`, np. „REA Nieruchomości”) jest osobno od nazwy prawnej (`legalName`,
 * „REA NIERUCHOMOŚCI SP. Z O.O.” — na fakturę). `nip`: polski NIP zapisany jako 10 cyfr albo zagraniczny numer VAT
 * z kodem kraju. `address`: ustrukturyzowany (adres jednym wierszem — starsze dane, MCP — jest rozkładany na pola).
 * `registrySource` / `registryCheckedAt` ustawia serwer tylko dla danych potwierdzonych w rejestrze (pamięć podręczna
 * zapytań); z przeglądarki przychodzą jako wskazanie „użyto danych z rejestru”.
 */
export const clientCompanySchema = z.object({
  legalName: z
    .string()
    .trim()
    .max(500, { message: 'Najwyżej 500 znaków' })
    .transform((v) => (v === '' ? undefined : v))
    .optional(),
  nip: optionalTaxIdSchema,
  regon: optionalRegonSchema,
  krs: optionalKrsSchema,
  vatStatus: z.enum(COMPANY_VAT_STATUSES).optional(),
  address: companyAddressInputSchema.optional(),
  website: urlSchema.optional(),
  registrySource: z.enum(COMPANY_REGISTRY_SOURCES).optional(),
  registryCheckedAt: z.string().datetime({ offset: true }).optional(),
});
export type ClientCompany = z.output<typeof clientCompanySchema>;
export type ClientCompanyInput = z.input<typeof clientCompanySchema>;

/**
 * Dane firmy z bazy do DTO: starszy adres jako tekst → pola, puste wartości pominięte. Bez walidacji (dane zapisane
 * wcześniej mogą nie spełniać dzisiejszych reguł — np. NIP sprzed sprawdzania sumy kontrolnej — i nadal się wyświetlają).
 */
export function normalizeClientCompany(raw: unknown): ClientCompany {
  if (!raw || typeof raw !== 'object') return {};
  const src = raw as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of ['legalName', 'nip', 'regon', 'krs', 'website', 'registryCheckedAt'] as const) {
    const v = src[key];
    if (typeof v === 'string' && v.trim() !== '') out[key] = v;
  }
  if (typeof src.vatStatus === 'string' && (COMPANY_VAT_STATUSES as readonly string[]).includes(src.vatStatus)) {
    out.vatStatus = src.vatStatus;
  }
  if (
    typeof src.registrySource === 'string' &&
    (COMPANY_REGISTRY_SOURCES as readonly string[]).includes(src.registrySource)
  ) {
    out.registrySource = src.registrySource;
  }
  if (typeof src.address === 'string') {
    const a = parseAddressLine(src.address);
    if (!isEmptyAddress(a)) out.address = a;
  } else if (src.address && typeof src.address === 'object') {
    const a = src.address as Record<string, unknown>;
    const s = (k: string) => (typeof a[k] === 'string' ? (a[k] as string) : '');
    const addr: CompanyAddress = {
      street: s('street'),
      buildingNo: s('buildingNo'),
      apartmentNo: s('apartmentNo'),
      postalCode: s('postalCode'),
      city: s('city'),
      country: s('country') || 'PL',
    };
    if (!isEmptyAddress(addr)) out.address = addr;
  }
  return out as ClientCompany;
}

/** `clients.links`: odnośniki do zasobów klienta (np. menedżer haseł, dysk, Figma). */
export const clientLinkSchema = z.object({
  label: z.string().trim().min(1).max(100),
  url: urlSchema,
});
export type ClientLink = z.infer<typeof clientLinkSchema>;

// ── Klient ──────────────────────────────────────────────────────────────────
/** Pola klienta edytowalne przez użytkownika (bez id, workspace_id, znaczników i archived_at). */
export const clientFieldsSchema = z.object({
  name: nameSchema,
  status: clientStatusSchema.default('active'),
  company: clientCompanySchema.default({}),
  /** stawka domyślna netto/h; wymaga flagi „Widzi stawki i kwoty” */
  defaultRate: rateSchema.nullable().optional(),
  currency: currencySchema.default('PLN'),
  color: hexColorSchema.nullable().optional(),
  notes: markdownSchema.nullable().optional(),
  links: z.array(clientLinkSchema).max(50).default([]),
  /** opiekun klienta (członek workspace'u); wyznacza zakres „Tylko przypisani” */
  ownerUserId: idSchema.nullable().optional(),
});
export type ClientFields = z.infer<typeof clientFieldsSchema>;

// ── Kontakt ─────────────────────────────────────────────────────────────────
export const contactFieldsSchema = z.object({
  clientId: idSchema,
  name: nameSchema,
  email: emailSchema.nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  /** rola osoby u klienta, np. „Marketing”, „Właściciel” */
  role: z.string().trim().max(100).nullable().optional(),
  /** główny kontakt klienta (najwyżej jeden aktywny na klienta: indeks unikalny w bazie) */
  isPrimary: z.boolean().default(false),
});
export type ContactFields = z.infer<typeof contactFieldsSchema>;

// ═══ Wejścia i wyjścia usług modułu crm (Fala 1, `packages/core/src/modules/crm`) ═══════════════════════

/**
 * Statusy ustawiane w formularzu. „Archiwalny” ustawia wyłącznie archiwizacja (`crm.archiveClient`), która
 * zapisuje też `archived_at`; przywrócenie (`crm.restoreClient`) wraca do jednego z tych statusów.
 */
export const CLIENT_EDITABLE_STATUSES = ['active', 'prospect', 'inactive'] as const;
export type ClientEditableStatus = (typeof CLIENT_EDITABLE_STATUSES)[number];
export const clientEditableStatusSchema = z.enum(CLIENT_EDITABLE_STATUSES);

/** Paleta kolorów klientów (docs/DESIGN.md §3: kolory klientów to dane). Dozwolony też dowolny `#RRGGBB`. */
export const CLIENT_COLOR_PALETTE = ['#2B3BFF', '#E8466B', '#6E56CF', '#14915A', '#12A5A5'] as const;

/** Kolor klienta zapisywany wielkimi literami (`#2b3bff` → `#2B3BFF`). */
export const clientColorSchema = hexColorSchema.transform((v) => v.toUpperCase());

/** Pole tekstowe opcjonalne: pusty tekst = brak wartości (null). */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, { message: `Najwyżej ${max} znaków` })
    .transform((v) => (v === '' ? null : v))
    .nullable();

const clientInputShape = {
  name: nameSchema,
  status: clientEditableStatusSchema,
  company: clientCompanySchema,
  /** wymaga flagi „Widzi stawki i kwoty” (bez niej FORBIDDEN) */
  defaultRate: rateSchema.nullable(),
  /** wymaga flagi „Widzi stawki i kwoty” (bez niej FORBIDDEN) */
  currency: currencySchema,
  color: clientColorSchema.nullable(),
  notes: markdownSchema.transform((v) => (v.trim() === '' ? null : v)).nullable(),
  links: z.array(clientLinkSchema).max(50, { message: 'Najwyżej 50 odnośników' }),
  /** opiekun: aktywny członek workspace'u; null = bez opiekuna */
  ownerUserId: idSchema.nullable(),
};

/** `crm.createClient`. Bez `ownerUserId` opiekunem zostaje osoba tworząca. */
export const createClientInput = z
  .object({
    name: clientInputShape.name,
    status: clientEditableStatusSchema.default('active'),
    company: clientCompanySchema.default({}),
    defaultRate: clientInputShape.defaultRate.optional(),
    currency: clientInputShape.currency.optional(),
    color: clientInputShape.color.optional(),
    notes: clientInputShape.notes.optional(),
    links: clientInputShape.links.default([]),
    ownerUserId: clientInputShape.ownerUserId.optional(),
  })
  .strict();
export type CreateClientInput = z.input<typeof createClientInput>;

/** `crm.updateClient`: tylko podane pola; `version` z ostatniego odczytu (inaczej CONFLICT). */
export const updateClientInput = z
  .object({
    id: idSchema,
    version: versionSchema,
    name: clientInputShape.name.optional(),
    status: clientInputShape.status.optional(),
    company: clientInputShape.company.optional(),
    defaultRate: clientInputShape.defaultRate.optional(),
    currency: clientInputShape.currency.optional(),
    color: clientInputShape.color.optional(),
    notes: clientInputShape.notes.optional(),
    links: clientInputShape.links.optional(),
    ownerUserId: clientInputShape.ownerUserId.optional(),
  })
  .strict();
export type UpdateClientInput = z.input<typeof updateClientInput>;

export const getClientInput = z.object({ id: idSchema }).strict();

/** `crm.archiveClient`: status „Archiwalny” + `archived_at`. `version` opcjonalna (gdy podana: CONFLICT przy zmianie). */
export const archiveClientInput = z.object({ id: idSchema, version: versionSchema.optional() }).strict();

/** `crm.restoreClient`: zdejmuje `archived_at`, status wraca do podanego (domyślnie „Aktywny”). */
export const restoreClientInput = z
  .object({ id: idSchema, version: versionSchema.optional(), status: clientEditableStatusSchema.default('active') })
  .strict();

/** `crm.setClientOwner`: zmiana opiekuna (wyznacza zakres „Tylko przypisani”). */
export const setClientOwnerInput = z
  .object({ id: idSchema, ownerUserId: idSchema.nullable(), version: versionSchema.optional() })
  .strict();

/**
 * `crm.listClients`. Bez `status`: klienci poza archiwum (z `includeArchived`: wszyscy).
 * `status: 'archived'` = archiwum. `ownerUserId: null` = bez opiekuna. `q` szuka w nazwie klienta, adresie strony
 * oraz w imionach i emailach widocznych kontaktów (bez polskich znaków).
 */
export const listClientsInput = z
  .object({
    status: clientStatusSchema.optional(),
    q: z.string().trim().max(200).optional(),
    ownerUserId: idSchema.nullable().optional(),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(200),
  })
  .strict()
  .default({});
export type ListClientsInput = z.input<typeof listClientsInput>;

/** `crm.listClientOptions` (ClientPicker w innych modułach): lekka lista klientów z zakresu osoby. */
export const listClientOptionsInput = z
  .object({ includeArchived: z.boolean().default(false), q: z.string().trim().max(200).optional() })
  .strict()
  .default({});

/** Osoba (opiekun) w DTO. */
export interface ClientPersonRef {
  userId: string;
  name: string;
  initials: string;
  avatarColor: string | null;
}

/**
 * Liczniki z innych modułów (null = brak podglądu modułu). Liczone w zakresie osoby.
 * `activeProjects`: projekty w statusie „W toku”; `openTasks`: zadania poza „Gotowe”; `overdueTasks`: otwarte
 * z terminem przed dziś (Europe/Warsaw); `pendingApprovals`: akceptacje czekające dla klienta.
 */
export interface ClientStats {
  activeProjects: number | null;
  openTasks: number | null;
  overdueTasks: number | null;
  pendingApprovals: number | null;
  /**
   * Kolumna „Do rozliczenia” (audyt końcowy Fali 6): kwota niezafakturowanego czasu (rozliczany, bez zestawienia; reguła
   * pozycji jak kafelek profilu i Rozliczenia) w głównej walucie; `null` = nic do rozliczenia. Pole tylko z „Widzi
   * stawki i kwoty” i modułem Czas albo Rozliczenia (bez nich pominięte, nie null).
   */
  unbilled?: { amount: string; currency: string } | null;
  /**
   * Kolumna „Ostatni kontakt”: ostatnia wiadomość z klientem (przychodząca albo wysłana; moduł Inbox, z prywatnością
   * skrzynki osobistej) albo spotkanie, które już się odbyło (moduł Klienci). `null` = brak albo bez tych modułów.
   */
  lastContactAt: Date | null;
}

/** Wspólne pola klienta w listach i karcie. Kwoty (`defaultRate`, `currency`) tylko z flagą seesRates (pominięte, nie null). */
export interface ClientSummaryDto {
  id: string;
  name: string;
  status: ClientStatus;
  color: string | null;
  /** strona www klienta (company.website) */
  website: string | null;
  ownerUserId: string | null;
  owner: ClientPersonRef | null;
  archivedAt: Date | null;
  version: number;
  /** tylko karta klienta (dostęp przez projekt albo zasób): bez kontaktów, usług i edycji */
  partial: boolean;
  defaultRate?: string | null;
  currency?: string;
}

export interface ClientListItem extends ClientSummaryDto {
  stats: ClientStats;
}

export interface ClientListOutput {
  items: ClientListItem[];
  /** liczba klientów w każdym statusie (zakres osoby, `q` i opiekun; bez filtra statusu) */
  counts: Record<ClientStatus, number>;
  /** klienci poza archiwum (nagłówek „Klienci 9”) */
  total: number;
}

/** Główny kontakt w karcie klienta (linia meta: „Anna Kowalczyk, anna@rea.pl”). */
export interface ClientPrimaryContact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
}

/** `crm.getClient`. W trybie częściowym (`partial`) bez notatek, odnośników, NIP/adresu i kontaktów. */
export interface ClientDto extends ClientSummaryDto {
  company: ClientCompany;
  notes: string | null;
  links: ClientLink[];
  createdAt: Date;
  updatedAt: Date;
  primaryContact: ClientPrimaryContact | null;
  /** nierozstrzygnięte konflikty emaili kontaktów tego klienta (widoczne dla osoby) */
  openConflicts: number;
  stats: ClientStats;
  /** czy osoba może edytować klienta (moduł Klienci: Edycja + zakres z edycją) */
  canEdit: boolean;
}

export interface ClientOption {
  id: string;
  name: string;
  color: string | null;
  status: ClientStatus;
  archived: boolean;
  partial: boolean;
}

// ── Kontakty ────────────────────────────────────────────────────────────────
const contactInputShape = {
  name: nameSchema,
  email: emailSchema.nullable(),
  phone: optionalText(40),
  role: optionalText(100),
  isPrimary: z.boolean(),
};

/**
 * `crm.createContact`. Bez `isPrimary` kontakt zostaje głównym, gdy klient nie ma jeszcze aktywnego głównego.
 * Ten sam email u innego klienta nie blokuje zapisu: wynik ma ostrzeżenie `EMAIL_CONFLICT`.
 */
export const createContactInput = z
  .object({
    clientId: idSchema,
    name: contactInputShape.name,
    email: contactInputShape.email.optional(),
    phone: contactInputShape.phone.optional(),
    role: contactInputShape.role.optional(),
    isPrimary: contactInputShape.isPrimary.optional(),
  })
  .strict();
export type CreateContactInput = z.input<typeof createContactInput>;

export const updateContactInput = z
  .object({
    id: idSchema,
    version: versionSchema,
    name: contactInputShape.name.optional(),
    email: contactInputShape.email.optional(),
    phone: contactInputShape.phone.optional(),
    role: contactInputShape.role.optional(),
    isPrimary: contactInputShape.isPrimary.optional(),
  })
  .strict();
export type UpdateContactInput = z.input<typeof updateContactInput>;

export const listContactsInput = z.object({ clientId: idSchema, includeArchived: z.boolean().default(false) }).strict();

/** Archiwizacja, przywrócenie, ustawienie głównego kontaktu. */
export const contactIdInput = z.object({ id: idSchema, version: versionSchema.optional() }).strict();

/** Inny kontakt z tym samym emailem (u innego klienta), widoczny dla osoby. */
export interface ContactConflictRef {
  contactId: string;
  contactName: string;
  clientId: string;
  clientName: string;
  clientColor: string | null;
}

export interface ContactDto {
  id: string;
  clientId: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  isPrimary: boolean;
  archivedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  version: number;
  /** ten sam email u innych (widocznych) klientów; pusta lista = brak konfliktu */
  conflicts: ContactConflictRef[];
  /** konflikt rozstrzygnięty jako „to różne osoby” (baner się nie pokazuje) */
  conflictResolved: boolean;
}

export const CONTACT_WARNING_CODES = ['EMAIL_CONFLICT'] as const;
export type ContactWarningCode = (typeof CONTACT_WARNING_CODES)[number];

export interface ContactWarning {
  code: ContactWarningCode;
  message: string;
  conflicts: ContactConflictRef[];
}

/** Wynik zapisu kontaktu: zapis + ostrzeżenia (nie blokują). */
export interface ContactMutationOutput {
  contact: ContactDto;
  warnings: ContactWarning[];
}

/** `crm.listContactConflicts`: grupy kontaktów z tym samym emailem u co najmniej dwóch klientów. */
export const listContactConflictsInput = z
  .object({ clientId: idSchema.optional(), includeResolved: z.boolean().default(false) })
  .strict()
  .default({});

export interface ContactConflictMember {
  contactId: string;
  name: string;
  role: string | null;
  isPrimary: boolean;
  clientId: string;
  clientName: string;
  clientColor: string | null;
  canEdit: boolean;
}

export interface ContactConflictGroup {
  email: string;
  /** rozstrzygnięty jako „to różne osoby” */
  resolved: boolean;
  resolvedAt: Date | null;
  contacts: ContactConflictMember[];
  /** osoba może rozstrzygnąć (edycja wszystkich aktywnych kontaktów z tym adresem) */
  canResolve: boolean;
}

/** Decyzje: „To ta sama osoba — zostaw u X” (pozostałe kontakty do archiwum) albo „To różne osoby — zostaw”. */
export const CONTACT_CONFLICT_DECISIONS = ['same_person', 'different_people'] as const;
export type ContactConflictDecision = (typeof CONTACT_CONFLICT_DECISIONS)[number];

export const resolveContactConflictInput = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('same_person'), email: emailSchema, keepContactId: idSchema }).strict(),
  z.object({ decision: z.literal('different_people'), email: emailSchema }).strict(),
]);
export type ResolveContactConflictInput = z.input<typeof resolveContactConflictInput>;

export interface ResolveContactConflictOutput {
  email: string;
  decision: ContactConflictDecision;
  /** kontakty przeniesione do archiwum („ta sama osoba”) */
  archivedContactIds: string[];
  /** kontakty oznaczone jako rozstrzygnięte („różne osoby”) */
  resolvedContactIds: string[];
}

/** Rozpoznanie klienta po emailu (moduł poczty, Fala 4): `matchClientsByEmail(tx, email)` z `@fos/core` (moduł crm). */
export interface ClientEmailMatch {
  clientIds: string[];
  /** więcej niż jeden klient (także po rozstrzygnięciu „różne osoby”): wiadomość wymaga wyboru klienta */
  ambiguous: boolean;
}
